import React, { useEffect, useRef, useCallback } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

Chart.register(...registerables);

interface AgricultureChartProps {
  location: string;
}

export const AgricultureChart: React.FC<AgricultureChartProps> = ({ location }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);
  const [noDataMsg, setNoDataMsg] = React.useState<string | null>(null);

  const createChart = useCallback(async () => {
    if (!chartRef.current) return;
    if (creatingRef.current) return;
    creatingRef.current = true;

    // Destroy existing chart (handles React 18 StrictMode double-mount too)
    try {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
      const existing = Chart.getChart(chartRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch (e) {
      // no-op
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Fetch county for selected location
    await supabaseLocationService.initialize();
    const countyName = supabaseLocationService.getCountyForCity(location) || location;

    // Try multiple query variants to maximize hit rate
    let rows: any[] | null = null;
    const baseCounty = (countyName || '').replace(' County', '').trim();

    // 1) county or geo_name OR match (no location_type filter to avoid mismatch)
    const q1 = await supabaseClient
      .from('ag_emissions')
      .select('*')
      .or(`county.ilike.%${baseCounty}%,geo_name.ilike.%${baseCounty}%,geo_name.ilike.%${baseCounty} County%`);
    if (q1.data && q1.data.length > 0) rows = q1.data;

    // 2) explicit state/county fallback
    if (!rows || rows.length === 0) {
      const q2 = await supabaseClient
        .from('ag_emissions')
        .select('*')
        .eq('state', 'California')
        .ilike('county', `%${baseCounty}%`);
      if (q2.data && q2.data.length > 0) rows = q2.data;
    }

    // If we found at least one matching row, lock to its geofips to ensure consistent county scope
    let countyGeofips: string | null = null;
    if (rows && rows.length > 0) {
      const first = rows.find(r => r.geofips && String(r.geofips).length <= 5) || rows[0];
      countyGeofips = String(first.geofips || '').trim();
      if (countyGeofips) {
        const { data: rowsByFips } = await supabaseClient
          .from('ag_emissions')
          .select('*')
          .eq('geofips', countyGeofips);
        if (rowsByFips && rowsByFips.length > 0) rows = rowsByFips;
      }
    }

    // Log what we got to help diagnose
    console.log('AgricultureChart:', { countyName, baseCounty, countyGeofips, rowCount: rows ? rows.length : 0 });

    // Build year list and group by var_name2
    const years = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
    const categoriesMap: Record<string, number[]> = {};
    (rows || []).forEach((r: any) => {
      const cat = r.var_name2 || 'Other';
      if (!categoriesMap[cat]) categoriesMap[cat] = years.map(() => 0);
      years.forEach((y, idx) => {
        const val = Number(r[String(y)]) || 0;
        // Scale emissions by 1,000,000 as requested
        categoriesMap[cat][idx] += val * 1_000_000;
      });
    });

    // Combine livestock categories into a single 'Livestock' series and place it at the bottom
    const livestockKeys = ['head_cattle','head_poultry','head_swine','head_other_livestock'];
    const hasLivestock = livestockKeys.some(k => categoriesMap[k]);
    if (hasLivestock) {
      const livestockTotals = years.map((_, idx) => 0);
      livestockKeys.forEach(key => {
        const arr = categoriesMap[key];
        if (arr) {
          arr.forEach((v, i) => livestockTotals[i] += v);
          delete categoriesMap[key];
        }
      });
      categoriesMap['Livestock'] = livestockTotals;
    }

    const palette = ['#60a5fa','#93c5fd','#2563eb','#34d399','#10b981','#f59e0b','#ef4444','#a78bfa','#f472b6'];
    const order = ['Livestock','fertilizer','rice','crop_burning','agriculture'];
    const entries = Object.entries(categoriesMap);
    entries.sort((a, b) => {
      const ia = order.indexOf(a[0]);
      const ib = order.indexOf(b[0]);
      if (ia === -1 && ib === -1) return a[0].localeCompare(b[0]);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    const datasets = entries.map(([cat, values], i) => ({
      label: cat,
      data: values,
      backgroundColor: palette[i % palette.length],
      borderColor: palette[i % palette.length],
      fill: true,
      tension: 0.2,
      borderWidth: 1,
      pointRadius: 0,
      pointHoverRadius: 4,
      stack: 'ag'
    }));
    const agricultureData = {
      labels: years.map(String),
      datasets,
    };

    // If no data, exit gracefully after ensuring chart is destroyed
    if ((datasets || []).length === 0) {
      setNoDataMsg(`No agricultural emissions data found for ${countyName}.`);
      creatingRef.current = false;
      return;
    }
    setNoDataMsg(null);

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: agricultureData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: `Agricultural Emissions by Source (${countyName})`,
            font: { size: 16, weight: 'bold' }
          },
          legend: {
            position: 'top',
            labels: { usePointStyle: true }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Year'
            }
          },
          y: {
            title: {
              display: true,
              text: 'Emissions (Metric Tons CO₂e)'
            },
            beginAtZero: true,
            stacked: true
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        }
      }
    });
    creatingRef.current = false;
  }, [location]);

  useEffect(() => {
    createChart();
    const chartNode = chartRef.current;
    return () => {
      try {
        if (chartInstance.current) {
          chartInstance.current.destroy();
          chartInstance.current = null;
        }
        if (chartNode) {
          const existing = Chart.getChart(chartNode as unknown as HTMLCanvasElement);
          if (existing) existing.destroy();
        }
      } catch (e) {}
    };
  }, [createChart]);

  return (
    <div className="chart-container">
      {noDataMsg ? (
        <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontStyle: 'italic' }}>
          {noDataMsg}
        </div>
      ) : (
        <canvas ref={chartRef} style={{ height: '300px' }} />
      )}
    </div>
  );
};
